import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

import { clearAuthTokens, getAccessToken } from '@/lib/tokenStorage';

let headers = {};

if (import.meta.env.VITE_DEV_TUNNEL_TOKEN) {
  headers = {
    'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
  };
}

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_GRAPHQL_API,
  credentials: 'include',
  headers,
  fetch: async (url, options) => {
    const response = await fetch(url, options);

    if (response.status === 401) {
      console.error('[Apollo Fetch]: 401 Unauthorized - Token expired');
      clearAuthTokens();
      window.location.href = '/login';
    }

    return response;
  },
});

const authLink = setContext((_, { headers }) => {
  const token = getAccessToken();
  const timezoneOffset = new Date().getTimezoneOffset();
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
      'X-Timezone-Offset': timezoneOffset.toString(),
    },
  };
});

const errorLink = onError(({ operation, graphQLErrors, networkError }) => {
  if (networkError) {
    console.log('[Apollo Network Error]:', networkError);
    const error = networkError as any;
    const status =
      error?.statusCode || error?.status || error?.response?.status;

    if (status === 401) {
      console.error('[Network Auth Error]: 401 Unauthorized - Token expired');
      clearAuthTokens();
      window.location.href = '/login';
      return;
    }
  }

  if (graphQLErrors) {
    console.log('[Apollo GraphQL Errors]:', graphQLErrors);
    for (const err of graphQLErrors) {
      //  add operationName to error extensions
      const mutableErr = err as { extensions?: Record<string, unknown> };
      mutableErr.extensions = {
        ...mutableErr.extensions,
        operationName: operation.operationName,
      };

      const extensions = err.extensions as Record<string, any>;

      if (
        extensions?.code === 'UNAUTHENTICATED' ||
        extensions?.statusCode === 401
      ) {
        console.error('[GraphQL Auth Error]: Token expired or invalid');
        clearAuthTokens();
        window.location.href = '/login';
        return;
      }
    }
  }
});

export const client = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
});
